import type { components } from '../../lib/api/schema.gen'
import { Table } from '../ui/Table'
import { roleLabel } from './roleLabels'

type Invitation = components['schemas']['Invitation']

export interface InvitationsTableProps {
  readonly invitations: readonly Invitation[]
  readonly revokingId: string | undefined
  readonly onRevoke: (id: string) => void
}

/**
 * Pending-invitations table (F0.6, PR-ONB-01) — `GET /onboarding/invitations`
 * (S0.9), RLS-scoped to the caller's tenant. The contract has no pagination
 * params (`apps/api/src/modules/onboarding/routes.ts` `listInvitations` is a
 * plain unpaginated `SELECT`), so this renders the full list as returned; a
 * large-account pagination need is a follow-up, not something to invent here.
 *
 * Only `status === 'pending'` invitations get a Revoke action — revoking an
 * already-accepted/expired/revoked invite has no effect server-side (the
 * neutral 404 case) and offering the button for those rows would just invite
 * a confusing error. Every row action is a real, always-visible `<button>`
 * (no hover-only reveal) per the Gate 2 UX spec §2.3 keyboard-access rule for
 * per-row actions in an admin table.
 */
export function InvitationsTable({ invitations, revokingId, onRevoke }: InvitationsTableProps) {
  return (
    <Table caption="Pending and past invitations" columnCount={5}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Email</Table.HeaderCell>
          <Table.HeaderCell>Role</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Expires</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {invitations.length === 0 && <Table.Empty title="No pending invitations." />}
        {invitations.map((invitation) => (
          <Table.Row key={invitation.id}>
            <Table.Cell>{invitation.email}</Table.Cell>
            <Table.Cell>{roleLabel(invitation.role)}</Table.Cell>
            <Table.Cell>
              <StatusPill status={invitation.status} />
            </Table.Cell>
            <Table.Cell>{formatDate(invitation.expiresAt)}</Table.Cell>
            <Table.Cell>
              {invitation.status === 'pending' ? (
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={revokingId === invitation.id}
                    onClick={() => onRevoke(invitation.id)}
                  >
                    {revokingId === invitation.id ? 'Revoking…' : 'Revoke'}
                  </button>
                </div>
              ) : (
                <span className="caption-text">No action available</span>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  )
}

function StatusPill({ status }: { readonly status: Invitation['status'] }) {
  const variant = status === 'pending' ? 'pending' : status === 'accepted' ? 'success' : 'error'
  const icon = status === 'pending' ? '◷' : status === 'accepted' ? '✓' : '✕'
  return (
    <span className={`status-pill status-pill-${variant}`}>
      <span aria-hidden="true">{icon}</span> {statusLabel(status)}
    </span>
  )
}

function statusLabel(status: Invitation['status']): string {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'accepted':
      return 'Accepted'
    case 'revoked':
      return 'Revoked'
    case 'expired':
      return 'Expired'
    default:
      return status
  }
}

function formatDate(isoDate: string): string {
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
